/**
 * Exploration engine entry — place-picker map into Hubert / Raleigh / Spanish Fork.
 */

import {
  DEFAULT_WORLD_ID,
  getWorld,
  validateWorld,
  WorldNavigator,
  WorldPrototypeView,
} from '@/world-prototype';
import { reapplyAllYardPresentations } from '@/world-prototype/worlds/jacksonvilleHouse';
import {
  clearOwnerEditSession,
  isOwnerEditSessionUnlocked,
  markOwnerEditUnlocked,
  shouldBypassPinGate,
  unlockOwnerEdit,
  wantsOwnerEditParam,
} from '@/world-prototype/ownerEdit/auth';
import { bootstrapLayoutOverrides, refreshRemoteLayoutOverrides } from '@/world-prototype/ownerEdit/layoutOverrides';
import { requestOwnerEditUnlock } from '@/world-prototype/ownerEdit/pinDialog';
import './styles.css';

function stripEditParamsFromUrl(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('edit') && !url.searchParams.has('ownerEdit')) {
    return;
  }
  url.searchParams.delete('edit');
  url.searchParams.delete('ownerEdit');
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, '', next);
}

async function tryMountEditor(
  view: WorldPrototypeView,
): Promise<boolean> {
  const params = new URLSearchParams(window.location.search);
  if (!wantsOwnerEditParam(params)) return false;

  const bypassPin = shouldBypassPinGate(params);
  let unlocked = isOwnerEditSessionUnlocked();
  if (!unlocked && bypassPin) {
    // Prefer API unlock so Save can write remote overrides (cross-device).
    // Falls back to local-only unlock if the API is unreachable.
    const unlock = await unlockOwnerEdit('');
    unlocked = unlock.ok;
    if (!unlocked) {
      markOwnerEditUnlocked(null);
      unlocked = true;
    }
  }

  if (!unlocked) {
    const result = await requestOwnerEditUnlock(async (pin) => {
      const unlock = await unlockOwnerEdit(pin);
      if (!unlock.ok) return { ok: false, error: unlock.error };
      return { ok: true };
    });
    if (!result.unlocked) return false;
    unlocked = true;
  }

  stripEditParamsFromUrl();

  const [{ EditorOverlay }, { createEditHost }] = await Promise.all([
    import('@/world-prototype/editor/EditorOverlay'),
    import('@/world-prototype/editor/createEditHost'),
  ]);
  new EditorOverlay(createEditHost(view), () => view.getCurrentSpace(), {
    ownerMode: true,
    devicePresentation: view.getBackyardPresentationMode() ?? 'desktop',
    onExit: () => {
      clearOwnerEditSession();
      window.location.reload();
    },
  });
  return true;
}

async function main(): Promise<void> {
  const mount = document.querySelector('#app');
  if (!(mount instanceof HTMLElement)) {
    throw new Error('#app mount missing');
  }

  // Load remote/bundled layout overrides before the first painted yard.
  await bootstrapLayoutOverrides();
  reapplyAllYardPresentations('desktop');

  const world = getWorld(DEFAULT_WORLD_ID);
  const errors = validateWorld(world);
  if (errors.length > 0) {
    console.error('[Exploration] graph errors:', errors);
  }

  const params = new URLSearchParams(window.location.search);
  const editIntent = wantsOwnerEditParam(params);
  // Prefer ?node=… . In World Editor, fall back to the last visited node so a
  // Save → Vite reload does not kick you out of Holly Springs / Hubert.
  // Outside edit mode, default to the places map.
  const nodeParam = params.get('node');
  const startOverride = nodeParam ?? (editIntent ? undefined : 'places-map');

  const navigator = new WorldNavigator(world, startOverride);
  const view = new WorldPrototypeView(mount, navigator);

  if (editIntent) {
    await tryMountEditor(view);
  } else {
    // Play mode: pull remote layout overrides when the phone returns to the app
    // after an editor Save on another device (no rebuild required).
    const pullRemote = async () => {
      const { changed } = await refreshRemoteLayoutOverrides();
      if (changed) window.location.reload();
    };
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void pullRemote();
    });
    window.addEventListener('pageshow', () => {
      void pullRemote();
    });
  }
}

void main();
